<template>
  <div class="confirm-registration-wrapper">
    <!-- Loading Spinner Overlay -->
    <v-overlay v-model="activeLoading" class="align-center justify-center" :persistent="true">
      <div class="loading-content text-center">
        <v-progress-circular color="primary" indeterminate size="64" width="4" />
      </div>
    </v-overlay>

    <!-- Main Content -->
    <div class="registration-content" :class="{ 'content-hidden': activeLoading }">
      <v-container style="padding-bottom: 0rem">
        <v-row wrap align="center" justify="center">
          <!-- Step indicator for mobile -->
          <v-col cols="12" class="d-flex d-lg-none" style="margin-bottom: 32px">
            <v-row wrap>
              <v-col cols="12">
                <v-row align="center" justify="center" style="margin-top: 20px" class="d-flex d-lg-none">
                  <v-icon
                    color="#FFC627"
                    style="font-size: 33px; padding-right: 25px; margin-right: 25px; border-right: 1px solid #dddddd"
                    >mdi-check-circle-outline</v-icon
                  >
                  <v-icon
                    color="#FFC627"
                    style="font-size: 33px; padding-right: 25px; margin-right: 15px; border-right: 1px solid #dddddd"
                    >mdi-check-circle-outline</v-icon
                  >
                  <span
                    class="yellowCircle"
                    style="padding-right: 10px; margin-right: -20px; height: 30px; max-width: 30px"
                    >3</span
                  >
                </v-row>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" md="7" lg="7" xl="7">
            <div class="confirm-text">Confirmation</div>

            <!-- User Information Card -->
            <v-card class="user-info-card" elevation="0" data-testid="user-info-card">
              <v-card-title class="card-title d-flex justify-space-between align-center">
                <span>User information</span>
                <span class="edit-link" @click="pushToRoute('Registerv2', true)" data-testid="edit-user-info-btn"
                  >Edit</span
                >
              </v-card-title>

              <v-card-text>
                <div class="info-section">
                  <h4 class="info-section-title">Personal information</h4>
                  <div class="info-row">
                    <span class="info-label">Name</span>
                    <span class="info-value">{{ fullName }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Email</span>
                    <span class="info-value">{{ contact.emailId || '' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Birthday</span>
                    <span class="info-value">{{ birthDate || '' }}</span>
                  </div>
                </div>

                <div class="info-section">
                  <h4 class="info-section-title">Contact information</h4>
                  <div class="info-row">
                    <span class="info-label">Address</span>
                    <span class="info-value">{{ mailingAddress }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Phone</span>
                    <span class="info-value">{{ learner.phoneNumber }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Country</span>
                    <span class="info-value">{{ learner.mailingAddress.country }}</span>
                  </div>
                  <div class="info-row" v-if="learner.isDiplomaOrGed">
                    <span class="info-label">High school diploma</span>
                    <span class="info-value">{{ learner.isDiplomaOrGed }}</span>
                  </div>
                  <div class="info-row" v-if="highSchoolGraduation">
                    <span class="info-label">Expected graduation</span>
                    <span class="info-value">{{ highSchoolGraduation }}</span>
                  </div>
                  <div class="info-row" v-if="learner.highSchoolType">
                    <span class="info-label">School type</span>
                    <span class="info-value">{{ learner.highSchoolType }}</span>
                  </div>
                  <div class="info-row" v-if="learner.currentHighSchool">
                    <span class="info-label">School</span>
                    <span class="info-value">{{ learner.currentHighSchool }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Consent Information -->
            <div class="consent-section">
              <p class="consent-text">
                By submitting my information, I understand that ASU will contact me about educational services via phone
                and email at the information provided. I consent to
                <a href="https://www.asu.edu/about/privacy" target="_blank" class="privacy-link"
                  >ASU's Privacy Statements</a
                >, including the European Supplement.
              </p>

              <v-checkbox v-model="formModel.permissionToText" class="consent-checkbox" color="#FFC627">
                <template v-slot:label>
                  <div class="consent-checkbox-text">
                    I consent to receive educational support from ASU via SMS/text messages, automated calls, and/or
                    prerecorded voice messages at the information provided above. I also consent to ASU's
                    <a href="https://asuonline.asu.edu/text-terms" target="_blank" class="privacy-link"
                      >mobile terms and conditions</a
                    >. I recognize that message and data rates may apply.
                  </div>
                </template>
              </v-checkbox>
            </div>

            <!-- Error Message -->
            <v-row v-if="errorMessage">
              <v-col cols="12" md="12" lg="12" xl="12">
                <v-row
                  style="background-color: #f7dddd; border: 1px solid #cc2f2f"
                  class="px-4 py-4 error-message"
                  outline
                >
                  <v-icon style="font-size: 1.4rem; align-items: start" class="mr-8" color="black">mdi-alert</v-icon>
                  <div class="ml-4" style="color: #191919; font-weight: normal" v-html="errorMessage" />
                </v-row>
              </v-col>
            </v-row>

            <!-- reCAPTCHA -->
            <v-row class="align-center justify-center" style="margin-top: 30px">
              <div class="form-group">
                <div
                  id="recaptcha-main"
                  class="g-recaptcha"
                  :data-sitekey="captchaSiteKey"
                  data-callback="captchaCallback"
                  data-expired-callback="captchaExpiredCallback"
                  data-error-callback="captchaErrorCallback"
                  data-action="LOGIN"
                />
              </div>
            </v-row>

            <!-- Action Buttons -->
            <v-row justify="space-between" align="center" style="margin-top: 30px">
              <v-col cols="auto">
                <button
                  class="registration-back-button"
                  @click="pushToRoute('Registerv2', false)"
                  data-testid="back-to-registration-btn"
                >
                  <v-icon style="margin-right: 8px; font-size: 20px">mdi-arrow-left</v-icon>
                  <span style="color: #2a2a2a">Back</span>
                </button>
              </v-col>

              <v-col cols="auto">
                <v-btn
                  :ripple="false"
                  variant="flat"
                  @click="captchaFirst($event)"
                  style="height: 48px; min-width: 160px"
                  :disabled="disableSubmit"
                  id="submit-btn"
                  data-testid="confirm-registration-btn"
                >
                  <span style="padding-right: 6px">Confirm</span>
                  <v-icon style="margin-left: 8px">mdi-arrow-right</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useUserStore } from '@/stores/user';
import { useRegistrationStore } from '@/stores/registration';
import { getParamValue } from '../util/param-persistence.js';
import { extractApiMessage } from '../util/api-error.js';
import { formatGraduationDate } from '@/utils/hsGraduation';

import { FetchPartnerData } from '../mixins/fetchPartnerDataMixin';
import { DataLayerMixin } from '../mixins/dataLayerMixin.js';
import { AjaxLoader } from '../mixins/ajaxLoader.js';

const GENERIC_SIGNUP_ERROR = 'We could not complete your registration. Please try again.';

export default defineComponent({
  name: 'ConfirmRegistrationv2',

  data() {
    return {
      contact: {},
      learner: {},
      formModel: {
        permissionToText: true
      },
      months: 'January February March April May June July August September October November December'.split(' '),
      errorMessage: '',
      disableSubmit: true,
      captchaWidgetId: undefined
    };
  },

  mixins: [FetchPartnerData, DataLayerMixin, AjaxLoader],

  mounted() {
    window.captchaCallback = this.captchaCallback;
    window.captchaExpiredCallback = this.resetCaptcha;
    window.captchaErrorCallback = this.resetCaptcha;
  },

  computed: {
    highSchoolGraduation() {
      if (this.learner.highSchoolGraduationDeclined) return 'Prefer not to provide';
      return formatGraduationDate(this.learner.highSchoolGraduationDate);
    },
    captchaSiteKey() {
      return import.meta.env.VITE_CAPTCHA_SITE_KEY;
    },

    welcomePageTitle() {
      const userStore = useUserStore();
      return userStore.welcomePageData.studentPortalTitle;
    },

    birthDate() {
      if (this.contact.birthDate) {
        const parts = this.contact.birthDate.split('-');
        return this.months[parseInt(parts[1]) - 1] + ' ' + parts[2] + ', ' + parts[0];
      }
      return 'NA';
    },

    mailingAddress() {
      if (this.learner.mailingAddress) {
        const addr = this.learner.mailingAddress;
        const addressParts = [];

        const street = addr.street || this.learner.place;
        const city = addr.city;
        const state = addr.state;
        const zipCode = addr.zipCode;

        // If the address was from HTML pre-fill, populate street from this.learner.place

        if (street) addressParts.push(street);
        if (city) addressParts.push(city);
        if (state) addressParts.push(state);
        if (zipCode) addressParts.push(zipCode);

        return addressParts.join(', ');
      }
      return '';
    },

    fullName() {
      return [this.contact.firstName, this.contact.middleName, this.contact.lastName].filter(Boolean).join(' ');
    },

    studentInfo() {
      return {
        first_name: this.contact.firstName,
        last_name: this.contact.lastName,
        email_address: this.contact.emailId,
        month: this.contact.birthDate ? this.contact.birthDate.split('-')[1] : '',
        day: this.contact.birthDate ? this.contact.birthDate.split('-')[2] : '',
        year: this.contact.birthDate ? this.contact.birthDate.split('-')[0] : '',
        pathway: this.contact.pathwayName,
        pathwayId: this.contact.pathwayId,
        address: this.mailingAddress,
        city: this.learner.mailingAddress ? this.learner.mailingAddress.city : '',
        state: this.learner.mailingAddress ? this.learner.mailingAddress.state : '',
        zip_code: this.learner.mailingAddress ? this.learner.mailingAddress.zipCode : '',
        country: this.learner.mailingAddress ? this.learner.mailingAddress.country : '',
        phone_number: this.learner.phoneNumber
      };
    }
  },

  methods: {
    captchaCallback(res) {
      if (res) {
        this.disableSubmit = false;
      }
    },

    // A reCAPTCHA token is single-use and expires after about two minutes, so any
    // path that leaves the learner on this page has to re-arm the widget before
    // they can submit again.
    resetCaptcha() {
      this.disableSubmit = true;

      try {
        window.grecaptcha?.reset(this.captchaWidgetId);
      } catch (error) {
        this.logError('resetCaptcha', error);
      }
    },

    initializeRecaptcha() {
      // Try to initialize reCAPTCHA with proper timing
      const attemptRender = (attempts = 0) => {
        if (attempts > 20) {
          // After 10 seconds (20 attempts * 500ms), give up
          this.errorMessage = 'Unable to load reCAPTCHA. Please refresh the page and try again.';
          this._recaptchaTimer = null;
          return;
        }

        if (typeof window !== 'undefined' && window.grecaptcha && window.grecaptcha.render) {
          try {
            // eslint-disable-next-line no-undef
            this.captchaWidgetId = grecaptcha.render('recaptcha-main');
            // Rendered successfully — clear the stale handle so cleanup is unambiguous
            this._recaptchaTimer = null;
          } catch (error) {
            // Retry if render fails
            this._recaptchaTimer = setTimeout(() => attemptRender(attempts + 1), 500);
          }
        } else {
          // reCAPTCHA not loaded yet, try again
          this._recaptchaTimer = setTimeout(() => attemptRender(attempts + 1), 500);
        }
      };

      // Start attempting to render reCAPTCHA
      this._recaptchaTimer = setTimeout(() => attemptRender(), 100);
    },

    pushToRoute(route, isEdit) {
      if (isEdit) {
        this.$router.push({ name: route, query: { fromRegisteration: isEdit } });
      } else {
        this.$router.push({ name: route });
      }
    },

    correctZero(num) {
      return num.length > 1 ? num : parseInt(num) < 10 ? '0' + num : num;
    },

    async captchaFirst() {
      let self = this;

      try {
        // Get the captcha token with multiple fallback methods
        let captchaToken = '';

        // Method 1: Try to get response from grecaptcha API
        if (window.grecaptcha) {
          try {
            captchaToken = window.grecaptcha.getResponse();
          } catch (error) {
            // console.log('Error getting captcha response from grecaptcha API:', error);
          }
        }

        // Method 2: Fallback to DOM element if API method failed
        if (!captchaToken) {
          const captchaElement = document.querySelector('.g-recaptcha-response');
          captchaToken = captchaElement ? captchaElement.value : '';
        }

        // Method 3: Try alternative selector
        if (!captchaToken) {
          const captchaTextarea = document.querySelector('textarea[name="g-recaptcha-response"]');
          captchaToken = captchaTextarea ? captchaTextarea.value : '';
        }

        if (!captchaToken || captchaToken.trim() === '') {
          self.errorMessage = 'Please complete the reCAPTCHA verification.';
          self.resetCaptcha();
          return;
        }

        return await self.createContactAndLearner(captchaToken);
      } catch (error) {
        // console.error('Captcha error:', error);

        self.errorMessage = 'There was an error with the captcha verification. Please try again.';
        self.resetCaptcha();
      }
    },

    createContactAndLearner(captchaToken) {
      this.asuoDataLayerClick('confirmRegisteration', '4');

      let self = this;
      this.errorMessage = '';

      let contactObject = JSON.parse(JSON.stringify(this.contact));

      if (contactObject.birthDate) {
        let date = contactObject.birthDate;
        contactObject.birthDate = [
          date.split('-')[0],
          this.correctZero(date.split('-')[1]),
          this.correctZero(date.split('-')[2])
        ].join('-');
      }

      const registrationStore = useRegistrationStore();
      let preservedParams = { ...registrationStore.getPreservedQueryParams() };

      // FALLBACK: Check requestedURL in localStorage for partner param
      // This ensures we capture partner even if it was lost during navigation
      // StudyHall opens links in new tab on welcome.ulc subdomain, so localStorage persists
      // Note: We only extract 'partner' for learner creation. course_offering_id is used
      // separately for post-login redirect to checkout (handled by requestedURL)
      const requestedURL = localStorage.getItem('requestedURL');

      if (requestedURL && requestedURL.includes('?')) {
        try {
          const url = new URL(requestedURL, window.location.origin);
          const urlParams = new URLSearchParams(url.search);

          // If partner is in requestedURL but not in preservedParams, use it as fallback
          if (urlParams.get('partner') && !preservedParams.partner) {
            preservedParams.partner = urlParams.get('partner');
            this.logInfo('Extracted partner from requestedURL:', urlParams.get('partner'));
          }
        } catch (e) {
          this.logError('Failed to parse requestedURL for params:', e);
        }
      }

      // Build UTM tracking data for Salesforce Form_Submission__c and Analytics_Metadata__c
      const utmTrackingData = {
        utm_source: getParamValue('utm_source'),
        utm_medium: getParamValue('utm_medium'),
        utm_campaign: getParamValue('utm_campaign'),
        utm_content: getParamValue('utm_content'),
        gclid: getParamValue('gclid'),
        fbclid: getParamValue('fbclid'),
        session_id: getParamValue('session_id'),
        landing_page: getParamValue('landing_page'),
        referrer_url: getParamValue('referrer_url'),
        submission_timestamp: new Date().toISOString()
      };

      // Month/year stay in the session so Back can restore them; the API only reads
      // highSchoolGraduationDate, which is blank when the learner declined.
      const learner = { ...this.learner };
      delete learner.highSchoolGraduationMonth;
      delete learner.highSchoolGraduationYear;

      return this.$conn
        .validateSignup(
          Object.assign(
            learner,
            contactObject,
            this.formModel,
            { captchaToken, captchaAction: 'LOGIN' },
            { _httpPreservedQueryParams: preservedParams },
            { _utmTrackingData: utmTrackingData }
          )
        )
        .then((data) => {
          if (data.errorMessage) {
            self.errorMessage = extractApiMessage({ response: { data } }, GENERIC_SIGNUP_ERROR);
            self.resetCaptcha();
          } else {
            self.$session.remove('contactFormModel');
            self.$session.remove('LearnerFormModel');

            // Clear registration store data since registration is complete
            const registrationStore = useRegistrationStore();
            registrationStore.setCurrentStep('success');
            registrationStore.clearRegistrationData();

            self.logInfo('learner data success, going to confirmation', data);
            // Store email in sessionStorage for SuccessRegistration page
            sessionStorage.setItem('registrationEmail', self.contact.emailId);
            self.$router.push({ name: 'SuccessRegistration', query: { email: self.contact.emailId } });
          }
        })
        .catch((err) => {
          // Only our own 4xx carries learner-facing copy. A 502/504 arrives with a
          // response too, but its body is the gateway's ("Endpoint request timed out"),
          // and a network error or 500 arrives as a bare TypeError with no response.
          self.errorMessage =
            err?.response?.status < 500 ? extractApiMessage(err, GENERIC_SIGNUP_ERROR) : GENERIC_SIGNUP_ERROR;

          self.resetCaptcha();
        });
    }
  },

  created() {
    const registrationStore = useRegistrationStore();
    registrationStore.setCurrentStep('confirm');

    const userStore = useUserStore();
    if (userStore.welcomePageData.welcomePageTitle == undefined) {
      this.fetchPartnerData();
    }

    if ((userStore.welcomePageData || {}).isEligibleForSignUp == false) {
      this.$router.push('/');
    }

    window.scrollTo(0, 0);

    this.contact = this.$session.get('contactFormModel') || {};
    this.learner = this.$session.get('LearnerFormModel') || {};

    if (this.contact.birthDate && this.contact.birthDate.includes('/')) {
      this.contact.birthDate =
        this.contact.birthDate.split('/')[2] +
        '-' +
        this.correctZero(this.contact.birthDate.split('/')[0]) +
        '-' +
        this.correctZero(this.contact.birthDate.split('/')[1]);
    }

    if (!this.contact.firstName) {
      this.$router.push({ name: 'Registerv2' });
    }

    // Initialize reCAPTCHA with better timing and error handling
    this.initializeRecaptcha();
  },

  beforeUnmount() {
    // Cancel any pending reCAPTCHA retry so it doesn't fire after teardown/navigation
    if (this._recaptchaTimer) {
      clearTimeout(this._recaptchaTimer);
      this._recaptchaTimer = null;
    }

    // The widget calls these by name off window, so drop the handles to this
    // instance rather than leaving them pointing at an unmounted component.
    delete window.captchaCallback;
    delete window.captchaExpiredCallback;
    delete window.captchaErrorCallback;
  }
});
</script>

<style lang="stylus" scoped>
.welcome-desktop2-header {
  font-size: 48px;
  font-weight: 900;
  padding: 0 7.79px;
  background-color: #FFC627;
  text-align: center;
}

.welcome-desktop2-registration {
  text-align: center;
  font-size: 36px;
  font-weight: 900;
  margin-top: 12px;

  @media (max-width: 992px) {
    font-size: 24px;
  }
}

.confirm-text {
  font-size: 32px;
  text-align: center;
  font-weight: 400;
  color: #191919;
  margin-bottom: 32px;
}

.user-info-card {
  margin-bottom: 32px;
}

.card-title {
  background-color: #f5f5f5;
  font-size: 18px;
  font-weight: 600;
  padding: 16px 24px;
}

.edit-link {
  color: #8C1D40;
  text-decoration: underline;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
}

.edit-link:hover {
  text-decoration: none;
}

.info-section {
  margin-bottom: 24px;
}

.info-section:last-child {
  margin-bottom: 0;
}

.info-section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
  margin-top:16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  color: #666;
  min-width: 120px;
}

.info-value {
  color: #333;
  text-align: right;
  word-break: break-word;
  flex: 1;
  margin-left: 16px;
}

.consent-section {
  margin: 32px 0;
}

.consent-text {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 16px;
}

.privacy-link {
  color: #8C1D40;
  text-decoration: underline;
}

.privacy-link:hover {
  text-decoration: none;
}

.consent-checkbox {
  margin-top: 16px;
}

.consent-checkbox-text {
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

.registration-back-button {
  font-size: 18px;
  border: 2px solid #2a2a2a;
  border-radius: 50px;
  font-weight: 400;
  padding: 0 24px;
  height: 48px;
  background: white;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
}

.registration-back-button:hover {
  background-color: #f5f5f5;
}

#submit-btn {
  border-radius: 50px;
  background-color: #fec627 !important;
  font-size: 18px;
  height: 48px;
}

.error-message a {
  color: #191919;
  text-decoration: underline;
}

.yellowCircle {
  background-color: #FFC627;
  color: #333;
  border-radius: 50%;
  width: 33px;
  height: 33px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

@media (max-width: 600px) {
  .welcome-desktop2-header {
    font-size: 30px;
  }

  .confirm-text {
    font-size: 24px;
    padding: 0 20px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-value {
    text-align: left;
    margin-left: 0;
    margin-top: 4px;
  }

  .registration-back-button {
    min-width: 100px;
    padding: 0 16px;
    font-size: 16px;
  }

  #submit-btn {
    min-width: 100px;
    font-size: 16px;
  }
}

/* Loading Overlay Styles */
.confirm-registration-wrapper {
  position: relative;
  min-height: 100vh;
}

.loading-content {
  /* Just the spinner, no background or padding needed */
}

.content-hidden {
  opacity: 0;
  pointer-events: none;
}

.registration-content {
  transition: opacity 0.3s ease;
}
</style>
